import React, { useState, useEffect } from 'react';
import { makeStyles } from "@material-ui/core/styles";
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import InputLabel from '@material-ui/core/InputLabel';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { Pencil } from 'lucide-react';
import FlagstoneTheme from '../../Config/flagstoneTheme';
import { useTranslation } from 'react-i18next';

export default function PercentagePieChart({ percentage, onPercentageChange, editable = false }) {
    const classes = useStyles();
    const { t } = useTranslation();
    const [isHovered, setIsHovered] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [tempPercentage, setTempPercentage] = useState(percentage);

    // Update tempPercentage when percentage prop changes
    useEffect(() => {
        setTempPercentage(percentage);
    }, [percentage]);

    // Calculate the stroke-dasharray for the progress circle
    const size = 48; // Total width/height
    const strokeWidth = 4;
    const radius = (size - strokeWidth) / 2;
    const innerRadius = radius - strokeWidth;
    const circumference = 2 * Math.PI * radius;
    const progressOffset = circumference - (percentage / 100) * circumference;

    const handleClick = () => {
        setTempPercentage(percentage);
        setDialogOpen(true);
    };

    const handleSave = () => {
        const newPercentage = Math.max(0, Math.min(100, parseFloat(tempPercentage) || 0));
        onPercentageChange(newPercentage);
        setDialogOpen(false);
    };

    const handleCancel = () => {
        setTempPercentage(percentage);
        setDialogOpen(false);
    };

    return (
        <>
            <div className={classes.container}>
                <svg width={size} height={size} className={classes.svg}>
                    {/* Background circle */}
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        stroke="#EDEDED"
                        strokeWidth={strokeWidth}
                    />
                    
                    {/* Progress circle */}
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        stroke={isHovered && editable ? "#C6EFC3" : "#3AB037"}
                        strokeWidth={strokeWidth}
                        strokeDasharray={circumference}
                        strokeDashoffset={progressOffset}
                        strokeLinecap="round"
                        transform={`rotate(-90 ${size / 2} ${size / 2})`}
                        className={classes.progressCircle}
                    />
                    
                    {/* Inner circle */}
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={innerRadius}
                        fill={isHovered && editable ? "#4654FB" : "white"}
                        className={classes.innerCircle}
                        onMouseEnter={() => editable && setIsHovered(true)}
                        onMouseLeave={() => editable && setIsHovered(false)}
                        onClick={() => editable && handleClick()}
                        style={{ cursor: editable ? 'pointer' : 'default' }}
                    />
                    
                    {/* Text or Icon */}
                    {!isHovered || !editable ? (
                        <text
                            x={size / 2}
                            y={size / 2}
                            textAnchor="middle"
                            dominantBaseline="central"
                            className={classes.percentageText}
                            onMouseEnter={() => editable && setIsHovered(true)}
                            onClick={() => editable && handleClick()}
                            style={{ cursor: editable ? 'pointer' : 'default', pointerEvents: 'none' }}
                        >
                            {Math.round(percentage)}%
                        </text>
                    ) : (
                        <g
                            transform={`translate(${size / 2 - 8}, ${size / 2 - 8})`}
                            onMouseLeave={() => editable && setIsHovered(false)}
                            onClick={() => editable && handleClick()}
                            style={{ cursor: 'pointer', pointerEvents: 'none' }}
                        >
                            <Pencil size={16} color="white" />
                        </g>
                    )}
                </svg>
            </div>

            {/* Edit Dialog */}
            <Dialog 
                className={classes.dialogContainer}
                open={dialogOpen} 
                onClose={handleCancel}
                aria-labelledby="dialog-title"
            >
                <DialogTitle className={classes.dialogTitle} id="dialog-title">
                    {t("Percentage aanpassen")}
                    <IconButton
                        aria-label="close"
                        className={classes.closeButton}
                        onClick={handleCancel}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <InputLabel className={classes.label}>
                        {t("Percentage")}
                    </InputLabel>
                    <TextField
                        className={classes.textField}
                        variant="outlined"
                        fullWidth
                        type="number"
                        value={tempPercentage}
                        onChange={(e) => setTempPercentage(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSave();
                            }
                        }}
                        inputProps={{ min: 0, max: 100, step: 1 }}
                        autoFocus
                    />
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <Button 
                        className={classes.cancelButton} 
                        disableRipple 
                        onClick={handleCancel}
                    >
                        {t("Annuleren")}
                    </Button>
                    <Button 
                        className={classes.submitButton} 
                        disableRipple 
                        onClick={handleSave}
                    >
                        {t("Opslaan")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

const useStyles = makeStyles({
    container: {
        display: 'inline-block',
    },
    svg: {
        display: 'block',
    },
    progressCircle: {
        transition: 'stroke 0.3s ease, stroke-dashoffset 0.6s ease',
    },
    innerCircle: {
        transition: 'fill 0.3s ease',
    },
    percentageText: {
        fontSize: '14px',
        fontFamily: 'Lato, sans-serif',
        fontWeight: 'medium',
        fill: '#020100',
        transition: 'all 0.6s ease',
    },
    dialogContainer: {
        '& .MuiDialog-paper': {
            width: "360px",
            maxWidth: "90vw",
            padding: "16px"
        }
    },
    dialogTitle: {
        color: "#000000",
        fontFamily: "Lato, sans-serif",
        fontWeight: 'medium',
        textAlign: "left",
        '& h2': {
            fontSize: "24px !important",
        },
        padding: "0",
        paddingBottom: "16px",
        position: "relative"
    },
    closeButton: {
        position: "absolute",
        right: "0",
        top: "0",
        color: "#000000"
    },
    dialogContent: {
        padding: "0",
        paddingBottom: "16px",
    },
    label: {
        fontSize: "14px",
        color: "#0C0E29",
        fontFamily: "Lato, sans-serif",
        marginBottom: "4px",
        display: "block"
    },
    textField: {
        '& .MuiOutlinedInput-root': {
            fontFamily: "Lato, sans-serif",
            fontSize: "16px",
            padding: "12px",
            '& fieldset': {
                borderColor: '#ddd',
                borderWidth: '1px',
            },
            '&:hover fieldset': {
                borderColor: FlagstoneTheme.palette.primary.main,
                borderWidth: '1px',
            },
            '&.Mui-focused fieldset': {
                borderColor: FlagstoneTheme.palette.primary.main,
                borderWidth: '1px',
            },
        },
        '& .MuiOutlinedInput-input': {
            padding: "0px",
        },
    },
    dialogActions: {
        justifyContent: "space-between",
        padding: "0",
    },
    cancelButton: {
        color: FlagstoneTheme.palette.primary.main,
        fontSize: "16px",
        fontFamily: "Lato, sans-serif",
        fontWeight: 'medium',
        backgroundColor: "white",
        border: `1px solid ${FlagstoneTheme.palette.primary.main}`,
        '&:hover': {
            backgroundColor: "#f5f5f5",
        },
        padding: "8px 20px",
        textTransform: "none",
    },
    submitButton: {
        color: "white",
        fontSize: "16px",
        fontFamily: "Lato, sans-serif",
        fontWeight: 'medium',
        backgroundColor: FlagstoneTheme.palette.primary.main,
        '&:hover': {
            backgroundColor: FlagstoneTheme.palette.primary.main,
            opacity: 0.9,
        },
        padding: "8px 20px",
        textTransform: "none",
    }
});
